/*
 * The BetterFX page's custom shortcuts section (sections/betterfx-keys.liquid).
 * First a shortcut is set the way the plugin does it: in the BetterFX panel's
 * General tab the pointer clicks the empty shortcut field next to Gaussian
 * Blur, it reads "Press a shortcut…", the keys go down and the field fills
 * in (and the effect joins Assigned Shortcuts). Then on the timeline two
 * clips are selected, the same keys are pressed and the effect lands on both
 * at once, no search. Then a custom preset goes on the title with its own
 * keys, and its keyframes play. Then it starts over.
 *
 * Plays only while on screen and while the tab is visible; leaving stops it
 * and it starts over next time. With reduced motion it shows the finished
 * result instead.
 */
(() => {
  const still = window.matchMedia('(prefers-reduced-motion: reduce)');
  const STOP = {};
  const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]);
  // Shortcuts are written the Mac way (⌥+⇧+B); on other systems the
  // modifiers read as Windows names them.
  const platform = (navigator.userAgentData && navigator.userAgentData.platform) || navigator.platform || navigator.userAgent;
  const isMac = /mac|iphone|ipad|ipod/i.test(platform);
  const WIN = { '⌘': 'Ctrl', '⌃': 'Ctrl', '⌥': 'Alt', '⇧': 'Shift' };
  const keyName = (k) => (isMac ? k : WIN[k] || k);
  const caps = (keys) => String(keys).split('+').map((k) => keyName(k.trim())).filter(Boolean);
  // How the BetterFX panel spells a shortcut in its field (formatShortcut
  // in the plugin): "Option + Shift + B" on a Mac, "Alt + Shift + B" elsewhere.
  const PANEL = isMac
    ? { '⌥': 'Option', '⇧': 'Shift', '⌘': 'Command', '⌃': 'Control' }
    : { '⌥': 'Alt', '⇧': 'Shift', '⌘': 'Control', '⌃': 'Control' };
  const panelLabel = (keys) =>
    String(keys)
      .split('+')
      .map((k) => PANEL[k.trim()] || k.trim())
      .filter(Boolean)
      .join(' + ');
  // Running text: "like ⌥⇧B" becomes "like Alt+Shift+B".
  const forOs = (text) =>
    isMac ? text : text.replace(/([⌃⌥⇧⌘]+)(\S)/g, (m, mods, key) => [...mods].map((c) => WIN[c]).concat(key).join('+'));

  const init = (root) => {
    if (root.dataset.bkReady) return;
    root.dataset.bkReady = 'true';

    const q = (sel) => root.querySelector(sel);
    const stage = q('[data-bk-stage]');
    const field = q('[data-bk-field]');
    if (!stage || !field) return;
    const target = q('[data-bk-target]');
    const newRow = q('[data-bk-newrow]');
    const hud = q('[data-bk-hud]');
    const capsEl = q('[data-bk-caps]');
    const hudName = q('[data-bk-hudname]');
    const hudSub = q('[data-bk-hudsub]');
    const cursor = q('[data-bk-cursor]');
    const screen = q('[data-bk-screen]');
    const title = q('[data-bk-title]');
    const playhead = q('[data-bk-playhead]');
    const clip = (name) => q(`[data-bk-clip="${name}"]`);
    const clips = [...root.querySelectorAll('[data-bk-clip]')];
    const effectKeys = field.dataset.keys || '⌥+⇧+B';
    const presetRow = q('[data-bk-preset]');
    const presetField = presetRow && presetRow.querySelector('[data-bk-keys]');
    const presetKeys = (presetField && presetField.dataset.bkKeys) || '⌥+⇧+1';
    const effectName = target.querySelector('.bk-ename span').textContent;
    const presetName = presetRow ? presetRow.querySelector('.bk-ename span').textContent : '';
    const appliedOne = root.dataset.appliedOne || '';
    const appliedMany = root.dataset.appliedMany || '';
    const applied = (n) => (n === 1 ? appliedOne : appliedMany.replace('[count]', n));

    root.querySelectorAll('[data-bk-os]').forEach((el) => (el.textContent = forOs(el.textContent)));
    root.querySelectorAll('[data-bk-keys]').forEach((el) => (el.textContent = panelLabel(el.dataset.bkKeys)));

    let run = 0;
    let playing = false;
    let inView = false;

    const wait = (r, ms) =>
      new Promise((resolve, reject) => {
        window.setTimeout(() => (r === run ? resolve() : reject(STOP)), ms);
      });

    // Where an element sits inside the stage, in px.
    const spot = (el, fx = 0.5, fy = 0.5) => {
      const s = stage.getBoundingClientRect();
      const b = el.getBoundingClientRect();
      return [b.left - s.left + b.width * fx, b.top - s.top + b.height * fy];
    };
    const moveTo = (r, el, fx, fy, ms = 650) => {
      const [x, y] = spot(el, fx, fy);
      cursor.style.setProperty('--cx', `${x}px`);
      cursor.style.setProperty('--cy', `${y}px`);
      return wait(r, ms);
    };
    const jumpTo = (el, fx, fy) => {
      cursor.style.transition = 'none';
      const [x, y] = spot(el, fx, fy);
      cursor.style.setProperty('--cx', `${x}px`);
      cursor.style.setProperty('--cy', `${y}px`);
      void cursor.offsetWidth;
      cursor.style.transition = '';
    };
    const tap = (r) => {
      cursor.classList.remove('is-click');
      void cursor.offsetWidth;
      cursor.classList.add('is-click');
      return wait(r, 200);
    };
    const restart = (el, cls) => {
      el.classList.remove(cls);
      void el.offsetWidth;
      el.classList.add(cls);
    };

    const showKeys = (keys, name) => {
      const list = caps(keys);
      capsEl.innerHTML = list
        .map((k) => `<span class="bk-cap${k.length > 1 ? ' is-word' : ''}">${esc(k)}</span>`)
        .join('<span class="bk-plus">+</span>');
      hudName.textContent = name;
      hudSub.classList.remove('is-on');
      hudSub.textContent = '';
      hud.classList.add('is-on');
    };
    const showApplied = (n) => {
      hudSub.textContent = applied(n);
      hudSub.classList.add('is-on');
    };
    const pressKeys = async (r) => {
      const all = [...capsEl.querySelectorAll('.bk-cap')];
      // modifiers go down first, the key last, like fingers do
      for (const cap of all) {
        cap.classList.add('is-down');
        await wait(r, 120);
      }
      await wait(r, 200);
      all.forEach((cap) => cap.classList.remove('is-down'));
    };

    const select = (names) => clips.forEach((c) => c.classList.toggle('is-selected', names.includes(c.dataset.bkClip)));
    const badge = (name) => restart(clip(name).querySelector('.bk-fx'), 'is-on');
    const setPlayhead = (p, play) => {
      playhead.classList.toggle('is-playing', Boolean(play));
      if (!play) {
        playhead.style.setProperty('--p', p);
        void playhead.offsetWidth;
      } else playhead.style.setProperty('--p', p);
    };
    const titleStart = () => {
      const c = clip('title');
      return c ? c.style.getPropertyValue('--x').trim() || '36%' : '36%';
    };

    const assign = () => {
      field.classList.remove('is-recording');
      field.classList.add('is-assigned');
      field.textContent = panelLabel(effectKeys);
    };

    const clear = () => {
      stage.classList.add('is-instant');
      stage.classList.remove('is-tl');
      target.classList.remove('is-hover');
      field.classList.remove('is-recording', 'is-assigned', 'is-saved');
      field.textContent = '';
      if (newRow) newRow.classList.remove('is-on');
      select([]);
      root.querySelectorAll('.bk-fx').forEach((fx) => fx.classList.remove('is-on'));
      screen.classList.remove('is-blur');
      title.classList.remove('is-slide');
      setPlayhead('47%', false);
      cursor.classList.remove('is-on', 'is-click');
      hud.classList.remove('is-on');
      hudSub.classList.remove('is-on');
      void stage.offsetWidth;
      stage.classList.remove('is-instant');
    };

    const loop = async (r) => {
      // ---- 1. Set the shortcut in the panel ----
      await wait(r, 500);
      jumpTo(target, 0.3, 2.4);
      cursor.classList.add('is-on');
      await wait(r, 250);
      await moveTo(r, target, 0.35, 0.55, 650);
      target.classList.add('is-hover');
      await moveTo(r, field, 0.5, 0.55, 520);
      await tap(r);
      field.classList.add('is-recording');
      field.textContent = root.dataset.i18nPress || 'Press a shortcut…';
      await wait(r, 650);
      showKeys(effectKeys, effectName);
      await wait(r, 420);
      await pressKeys(r);
      assign();
      restart(field, 'is-saved');
      await wait(r, 450);
      if (newRow) newRow.classList.add('is-on');
      await wait(r, 1200);
      target.classList.remove('is-hover');
      hud.classList.remove('is-on');

      // ---- 2. Use it on the timeline ----
      if (getComputedStyle(stage).gridTemplateColumns.split(' ').length < 2) {
        // one window on phones: switch to the timeline
        cursor.classList.remove('is-on');
        await wait(r, 200);
        stage.classList.add('is-tl');
        await wait(r, 450);
        jumpTo(clip('longboard'), 0.4, 2.2);
        cursor.classList.add('is-on');
        await wait(r, 200);
      }
      await moveTo(r, clip('longboard'), 0.55, 0.55, 750);
      await tap(r);
      select(['longboard']);
      await wait(r, 250);
      await moveTo(r, clip('sunset'), 0.5, 0.55, 520);
      await tap(r);
      select(['longboard', 'sunset']);
      await wait(r, 350);
      showKeys(effectKeys, effectName);
      await wait(r, 450);
      await pressKeys(r);
      badge('longboard');
      badge('sunset');
      screen.classList.add('is-blur');
      showApplied(2);
      await wait(r, 1900);

      // ---- 3. A custom preset, keyframes and all ----
      hud.classList.remove('is-on');
      await moveTo(r, clip('title'), 0.5, 0.55, 650);
      await tap(r);
      select(['title']);
      await wait(r, 300);
      showKeys(presetKeys, presetName);
      await wait(r, 450);
      await pressKeys(r);
      badge('title');
      showApplied(1);
      await wait(r, 350);
      cursor.classList.remove('is-on');
      // play it from the start of the title
      setPlayhead(titleStart(), false);
      title.classList.remove('is-slide');
      title.style.transform = 'translateX(-160%)';
      await wait(r, 260);
      title.style.transform = '';
      restart(title, 'is-slide');
      setPlayhead('47%', true);
      await wait(r, 2600);

      // ---- and again ----
      hud.classList.remove('is-on');
      stage.style.opacity = '0.35';
      await wait(r, 380);
      clear();
      stage.style.opacity = '';
      await wait(r, 500);
    };

    const play = async () => {
      run += 1;
      const r = run;
      playing = true;
      clear();
      try {
        for (;;) await loop(r);
      } catch (error) {
        if (error !== STOP) throw error;
      }
    };

    const stop = () => {
      run += 1;
      playing = false;
      stage.style.opacity = '';
      title.style.transform = '';
      clear();
    };

    // reduced motion: the shortcut set and used
    const finished = () => {
      assign();
      if (newRow) newRow.classList.add('is-on');
      select(['longboard', 'sunset']);
      ['longboard', 'sunset', 'title'].forEach((name) => clip(name).querySelector('.bk-fx').classList.add('is-on'));
      screen.classList.add('is-blur');
      showKeys(effectKeys, effectName);
      showApplied(2);
    };

    if (still.matches || !('IntersectionObserver' in window)) {
      finished();
      return;
    }

    const update = () => {
      const go = inView && !document.hidden;
      if (go && !playing) play();
      else if (!go && playing) stop();
    };

    new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          inView = entry.isIntersecting;
        });
        update();
      },
      { threshold: 0.4 }
    ).observe(stage);
    document.addEventListener('visibilitychange', update);
  };

  const boot = () => document.querySelectorAll('[data-betterfx-keys]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
